import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import BasicDialog from "../../../common/components/functional/dialogs/BasicDialog";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import { Nullable } from "../../../common/types/nullable";
import {
  EditEntry,
  Entry,
  Leave,
  LeaveEntity,
  LeaveEntityDateRange,
  NewEntryTalentData,
} from "../entriesAPI";
import { clearAbsenceEntries } from "../entriesSlice";
import Absences from "../Entry/Absences/Absences";
import ConvertToAbsence from "../Entry/Absences/ConvertToAbsence/ConvertToAbsence";
import EditEntryView from "../Entry/EditEntry/EditEntryView";

interface Props {
  selectedEntry: Entry | null;
  setSelectedEntry: (val: Entry | null) => void;
  setNewEntry: (val: Nullable<EditEntry>) => void;
  newEntry: Nullable<EditEntry>;
  openCopyModal: (entry: Entry) => void;
  newAbsence: boolean;
  setNewAbsence: (val: boolean) => void;
  newEntryTalentData?: NewEntryTalentData;
}

const EntriesModals = ({
  selectedEntry,
  setSelectedEntry,
  newEntry,
  setNewEntry,
  openCopyModal,
  newAbsence,
  setNewAbsence,
  newEntryTalentData,
}: Props) => {
  const { t } = useTranslation();

  const initialLeaveEntity: LeaveEntity = {
    id: null,
    version: 0,
    talentId: null,
    type: "",
    dateRange: { startDate: null, endDate: null },
    continuationOf: null,
    continuationOfId: null,
    entries: [],
    previousLeaves: { order: [], chains: {} },
  };

  const [leaveEntity, setLeaveEntity] = useState<LeaveEntity>({
    ...initialLeaveEntity,
  });

  const [convertModalOpen, setConvertModalOpen] = useState<boolean>(false);

  const { translatedEntryTypes } = useAppSelector((state: any) => ({
    translatedEntryTypes: state.localized.entryType,
  }));

  const dispatch = useAppDispatch();

  const clearLeaveEntity = () => {
    setLeaveEntity({ ...initialLeaveEntity });
  };

  const updateLeaveEntity = useCallback(
    (key: string, value: string | LeaveEntityDateRange | Leave | null) => {
      setLeaveEntity((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  const closeConvertModal = (clickedContinue?: boolean) => {
    setConvertModalOpen(false);

    // If clickedContinue is false or undefined, reset setNewAbsence to false
    if (!clickedContinue) {
      setNewAbsence(false);
    }
  };

  const closeModal = () => {
    setSelectedEntry(null);
    setNewEntry(null);
    setLeaveEntity({ ...initialLeaveEntity });
    setConvertModalOpen(false);
    setNewAbsence(false);
    // Empty absence entries from redux store
    dispatch(clearAbsenceEntries());
  };

  const modalSelector = (entry: Entry | EditEntry | null) => {
    switch (entry?.type) {
      case "WORK_ENTRY":
        return (
          <EditEntryView
            selectedEntry={entry}
            setConvertModalOpen={setConvertModalOpen}
            setCopyModalOpen={openCopyModal}
            cancel={closeModal}
          />
        );
      default:
        return (
          <Absences
            selectedEntry={selectedEntry}
            closeModal={closeModal}
            translatedEntryTypes={translatedEntryTypes}
            leaveEntity={leaveEntity}
            setLeaveEntity={setLeaveEntity}
            updateLeaveEntity={updateLeaveEntity}
            clearLeaveEntity={clearLeaveEntity}
            newEntryTalentData={newEntryTalentData}
          />
        );
    }
  };

  return (
    <>
      <DialogComponent
        variant="card"
        open={!!selectedEntry || !!newEntry}
        autoWidth
        DialogContentProps={{ sx: { padding: "0px" } }}
      >
        {modalSelector(selectedEntry || newEntry)}
      </DialogComponent>
      {(selectedEntry || newAbsence) && (
        <BasicDialog
          title={t("hourEntries:absence")}
          open={convertModalOpen || newAbsence}
          hideActions
          noBorder
          content={
            <ConvertToAbsence
              entry={selectedEntry}
              setSelectedEntry={setSelectedEntry}
              closeConvertModal={closeConvertModal}
              leaveEntity={leaveEntity}
              updateLeaveEntity={updateLeaveEntity}
              clearLeaveEntity={clearLeaveEntity}
              setLeaveEntity={setLeaveEntity}
              translatedEntryTypes={translatedEntryTypes}
              talentId={newEntryTalentData?.id}
              createNewAbsence={newAbsence}
            />
          }
        />
      )}
    </>
  );
};

export default EntriesModals;
